import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ConsoleLayout from '../layouts/ConsoleLayout';
import { Button } from '@/components/ui/button';
import { 
  BookOpen, 
  Clock, 
  TrendingUp, 
  Play,
  Library,
  MessageSquare
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/config/api';

// BACKEND_URL imported from centralized config
const API = `${BACKEND_URL}/api`;

const ReaderConsole = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [library, setLibrary] = useState([]);
  const [summary, setSummary] = useState({
    total_books_in_library: 0,
    total_minutes_listened: 0,
    total_engagement_units: 0,
    credits: { total: 0, active: 0, passive: 0, version_id: '' }
  });

  useEffect(() => {
    loadReaderData();
  }, []);

  const loadReaderData = async () => {
    try {
      setLoading(true);
      
      // Get auth token
      const token = localStorage.getItem('accessToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Fetch library and summary in parallel
      const [libraryRes, summaryRes] = await Promise.all([
        axios.get(`${API}/reader/library`, { headers }),
        axios.get(`${API}/reader/summary`, { headers })
      ]);
      
      // API returns { library: [...] }
      const libraryData = libraryRes.data?.library || libraryRes.data || [];
      setLibrary(libraryData);
      setSummary(summaryRes.data || {
        total_books_in_library: 0,
        total_minutes_listened: 0,
        total_engagement_units: 0,
        credits: { total: 0, active: 0, passive: 0, version_id: '' }
      });
    } catch (error) {
      console.error('Error loading reader data:', error);
      toast.error('Failed to load your library');
    } finally {
      setLoading(false);
    }
  };

  const handleStartListening = async (book) => {
    try {
      // Get auth token
      const token = localStorage.getItem('accessToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // First, open/create a session for this manuscript
      const response = await axios.post(`${API}/manuscripts/${book.manuscript_id}/open`, {}, { headers });
      const { session_id } = response.data;
      
      console.log('[ReaderConsole] Navigating to player with:', {
        session_id,
        returnTo: '/reader',
        role: 'reader'
      });
      
      // Navigate to player with session_id and returnTo state
      navigate(`/player/${session_id}`, {
        state: {
          returnTo: '/reader-console',
          role: 'reader'
        }
      });
    } catch (error) {
      console.error('[ReaderConsole] Error opening manuscript:', error);
      toast.error('Failed to open book for listening');
    }
  };

  const handleBrowseLibrary = () => {
    navigate('/library');
  };

  return (
    <ConsoleLayout pillarLabel="Reader">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            My NewAudia
          </h1>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-3xl">
            Your personal listening hub. Pick up where you left off, or discover a new NFD-SmartBook<sup className="text-[0.6em]">™</sup>.
          </p>
        </div>

        {/* Stats Strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {/* Total Minutes */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {loading ? '...' : Math.round(summary.total_minutes_listened)}
                </p>
                <p className="text-sm text-gray-500">Minutes Listened</p>
              </div>
            </div>
          </div>

          {/* Total Credits */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {loading ? '...' : (summary.credits?.total ?? 0).toFixed(2)}
                </p>
                <p className="text-sm text-gray-500">Credits Used</p>
              </div>
            </div>
          </div>

          {/* Books in Library */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {loading ? '...' : summary.total_books_in_library}
                </p>
                <p className="text-sm text-gray-500">Books in Library</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Row */}
        <div className="mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 bg-gradient-to-r from-purple-50 to-cyan-50 rounded-xl border border-purple-100">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Discover More NFD-SmartBooks<sup className="text-[0.6em]">™</sup>
            </h3>
            <p className="text-sm text-gray-600">
              Find your next NFD-SmartBook<sup className="text-[0.6em]">™</sup> to add to your library
            </p>
          </div>
          <Button
            onClick={handleBrowseLibrary}
            className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white shadow-lg"
          >
            <Library className="w-4 h-4 mr-2" />
            Browse NewAudia Library
          </Button>
        </div>

        {/* Personal Library Section */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Your Personal Library
          </h2>

          {/* Loading State */}
          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-xl p-4 shadow-sm animate-pulse">
                  <div className="aspect-[3/4] bg-gray-200 rounded-lg mb-3"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && library.length === 0 && (
            <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-10 h-10 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Your library is empty
              </h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Browse the NewAudia Library to add your first NFD-SmartBook<sup className="text-[0.6em]">™</sup>.
              </p>
              <Button
                onClick={handleBrowseLibrary}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Library className="w-4 h-4 mr-2" />
                Browse NewAudia Library
              </Button>
            </div>
          )}

          {/* Library Grid */}
          {!loading && library.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {library.map((book) => (
                <div
                  key={book.manuscriptId}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden border border-gray-100"
                >
                  {/* Cover Image */}
                  <div className="aspect-[3/4] overflow-hidden bg-gradient-to-br from-purple-100 to-cyan-100">
                    <img
                      src={book.coverImageUrl ? `${BACKEND_URL}${book.coverImageUrl}?v=${book.cover_updated_at || Date.now()}` : '/newaudia-logo.jpg'}
                      alt={book.title}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Book Info */}
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 text-sm line-clamp-2 leading-tight mb-1">
                      {book.title}
                    </h3>
                    <p className="text-xs text-gray-600 mb-3 line-clamp-1">
                      {book.author}
                    </p>

                    {/* Progress/Status */}
                    <div className="mb-3">
                      {book.bookmark ? (
                        <div>
                          <div className="flex justify-between text-xs text-gray-600 mb-1">
                            <span>Progress</span>
                            <span>Ch. {(book.bookmark?.chapterIndex ?? 0) + 1}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-purple-600 to-cyan-600 h-2 rounded-full transition-all"
                              style={{ width: `${Math.min(100, ((book.bookmark?.chapterIndex ?? 0) + 1) * 10)}%` }}
                            ></div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 italic">Not started</p>
                      )}
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-3 mb-3 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" />
                        <span>{(book.credits?.total ?? 0).toFixed(2)} credits</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" />
                        <span>{book.audiaQuestions ?? 0} Qs</span>
                      </div>
                    </div>

                    {/* Action Button */}
                    <Button
                      onClick={() => handleStartListening({ ...book, manuscript_id: book.manuscriptId })}
                      className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white"
                      size="sm"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      {book.bookmark ? 'Continue Listening' : 'Start Listening'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </ConsoleLayout>
  );
};

export default ReaderConsole;
